import React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { formatNumberWithoutDecimals } from 'utils/helpers/format';
import { bool, func, shape, string } from 'prop-types';
import { format, parseISO } from 'date-fns';
import * as R from 'ramda';
import { useNavigate } from 'react-router-dom';

import { useGetDailyProjectionQuery } from '../dashboardEndpoints';
import MinDonut from './MinDonut';

const FutureProjectionDrawer = ({
  isDrawerOpen,
  params,
  selectedDate,
  setIsDrawerOpen,
  setSelectedDate,
}) => {
  const navigate = useNavigate();

  const handleClose = () => {
    setIsDrawerOpen(false);
    setSelectedDate(null);
  };

  const { data = {} } = useGetDailyProjectionQuery(
    { params: { ...params, date: selectedDate } },
    { skip: R.isNil(selectedDate) }
  );

  const donutData = {
    additionalVolumeNeeded: R.prop('additionalVolumeNeeded', data),
    currentMin: R.prop('currentMin', data),
    excessVolume: R.prop('excessVolume', data),
    overAllocation: R.prop('overAllocation', data),
    projections: R.prop('projection', data),
    underAllocation: R.prop('underAllocation', data),
  };

  const transactions = R.defaultTo([], R.prop('transactions', data));

  return (
    <Drawer
      anchor="right"
      onClose={() => handleClose()}
      open={isDrawerOpen}
      slotProps={{
        paper: {
          sx: {
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            borderBottomLeftRadius: 5,
            borderTopLeftRadius: 5,
            overflowX: 'hidden',
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            width: { xs: '100%', lg: 480 },
          },
        },
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          boxShadow: 1,
          display: 'flex',
          justifyContent: 'space-between',
          pl: 2,
        }}
      >
        <Typography variant="h6">View Projection</Typography>
        <IconButton
          onClick={() => handleClose()}
          size="large"
          sx={{
            color: 'secondary.medium',
          }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </Box>
      <Box
        sx={{
          '&::-webkit-scrollbar': {
            display: 'none',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          },
          height: 'calc(100% - 129px)',
          overflowY: 'auto',
        }}
      >
        <Stack spacing={2} sx={{ p: 3 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">Future Date</Typography>
            {selectedDate && (
              <Typography variant="body2">
                {format(parseISO(selectedDate), 'MM/dd/yyyy')}
              </Typography>
            )}
          </Stack>
          {R.not(R.isEmpty(data)) && (
            <Box sx={{ alignSelf: 'center' }}>
              <MinDonut data={donutData} />
            </Box>
          )}
          {R.prop('overAllocation', data) && (
            <Stack
              alignItems="center"
              direction="row"
              spacing={2}
              sx={{ backgroundColor: 'warning.light', p: 2 }}
            >
              <ErrorIcon sx={{ color: 'warning.main' }} />
              <Typography variant="body2">
                You will be {R.prop('overAllocation', data) ? 'over' : 'under'} your Maximum
                Allocation on this date. Contact&nbsp;
                <Link
                  href="mailto:ShipperServices@magellanlp.com?subject=Allocation"
                  sx={{ color: 'secondary.main', textDecoration: 'none' }}
                >
                  ShipperServices@magellanlp.com
                </Link>
                &nbsp;for contract storage, or click on the button below to adjust your volumes.
              </Typography>
            </Stack>
          )}
          {R.prop('underAllocation', data) && (
            <Stack
              alignItems="center"
              direction="row"
              spacing={2}
              sx={{ backgroundColor: 'warning.light', p: 2 }}
            >
              <ErrorIcon sx={{ color: 'warning.main' }} />
              <Typography variant="body2">
                You will be under your Minimum Allocation on this date. Click the button below to
                adjust your volumes.
              </Typography>
            </Stack>
          )}
        </Stack>
        {!!transactions.length && (
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                borderBottom: '1px solid',
                borderBottomColor: 'graphic.light',
                borderTop: '1px solid',
                borderTopColor: 'graphic.light',
              }}
            >
              <Stack direction="row" spacing={1}>
                <Typography variant="body2">Pending Transactions</Typography>
                <Chip label={transactions.length} type="accordion" variant="badge" />
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2 }}>
              <Box
                sx={{ display: 'grid', gridGap: 8, gridTemplateColumns: 'repeat(4, 1fr)', mb: 3 }}
              >
                <Typography variant="caption">Transaction ID</Typography>
                <Typography variant="caption">Shipment Request ID</Typography>
                <Typography variant="caption">Type</Typography>
                <Typography variant="caption">Volume</Typography>
              </Box>
              <Box sx={{ display: 'grid', gridAutoRows: '1fr', gridRowGap: 16 }}>
                {transactions.map(
                  ({ shipmentRequestId, transactionId, transactionVolume, type }) => (
                    <Box
                      key={transactionId}
                      sx={{ display: 'grid', gridGap: 8, gridTemplateColumns: 'repeat(4, 1fr)' }}
                    >
                      <Typography variant="subtitle2">{transactionId}</Typography>
                      <Typography variant="subtitle2">{shipmentRequestId}</Typography>
                      <Typography variant="subtitle2">{type}</Typography>
                      <Typography variant="subtitle2">
                        {formatNumberWithoutDecimals(transactionVolume)} bbl
                      </Typography>
                    </Box>
                  )
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
      <Box
        sx={{
          backgroundColor: 'surface.white',
          bottom: 0,
          boxShadow: 0,
          display: 'flex',
          justifyContent: 'center',
          position: 'fixed',
          px: 3,
          py: '18px',
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix
          width: { xs: '100%', lg: 480 },
          zIndex: 1,
        }}
      >
        <Button onClick={() => handleClose()} sx={{ mr: 3, width: 1 }} variant="outlined">
          Close
        </Button>
        {R.prop('overAllocation', data) ? (
          <Button onClick={() => navigate('../../product-ownership/open')} sx={{ width: 1 }}>
            Go to PTOs
          </Button>
        ) : R.prop('underAllocation', data) ? (
          <Button onClick={() => navigate('../../reconsignments')} sx={{ width: 1 }}>
            Go to Reconsignments
          </Button>
        ) : null}
      </Box>
    </Drawer>
  );
};

FutureProjectionDrawer.propTypes = {
  isDrawerOpen: bool.isRequired,
  params: shape({}).isRequired,
  selectedDate: string,
  setIsDrawerOpen: func.isRequired,
  setSelectedDate: func.isRequired,
};

FutureProjectionDrawer.defaultProps = {
  selectedDate: null,
};

export default FutureProjectionDrawer;
